import { useCallback, useMemo, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useGameSocket } from "../hooks/useGameSocket"
import { haptic } from "../telegram/webApp"
import { getCurrentUserId } from "../api/client"

// Mirrors PublicGameStateDTO from worker/src/websocket/protocol.ts. Duplicated
// (not imported) because the web app and worker are separate TS projects; see
// the note in hooks/useGameSocket.ts.
type Player = { userId: string; username: string; color: string; seatIndex: number; isActive: boolean; finishedPieces: number }
type Piece = { id: string; color: string; state: string; position: number }
type PublicGameState = {
	gameId: string
	status: string
	players: Player[]
	pieces: Piece[]
	currentTurn: { turnNumber: number; playerId: string; status: string; diceResult: number | null; startedAt: number }
	winnerUserId: string | null
	version: number
}

const COLORS: Record<string, string> = {
	RED: "#ef4444",
	BLUE: "#3b82f6",
	GREEN: "#22c55e",
	YELLOW: "#eab308",
}

// Home "parking" slot positions per color, purely for layout (not game logic).
const HOME_SLOTS: Record<string, Array<[number, number]>> = {
	RED: [[1.5, 1.5], [3, 1.5], [1.5, 3], [3, 3]],
	BLUE: [[11, 1.5], [12.5, 1.5], [11, 3], [12.5, 3]],
	GREEN: [[11, 11], [12.5, 11], [11, 12.5], [12.5, 12.5]],
	YELLOW: [[1.5, 11], [3, 11], [1.5, 12.5], [3, 12.5]],
}

// The 52-square shared track, walked in this order, purely for rendering;
// the server is the sole authority on legality. Built once at module load
// so both the piece layer and the board-cell grid stay perfectly aligned.
const RING: Array<[number, number]> = []
for (let i = 0; i < 6; i++) RING.push([6 + i, 0])
for (let i = 0; i < 6; i++) RING.push([14, i])
for (let i = 0; i < 6; i++) RING.push([14 - i, 6])
for (let i = 0; i < 6; i++) RING.push([8, 6 + i])
for (let i = 0; i < 6; i++) RING.push([8 - i, 14])
for (let i = 0; i < 6; i++) RING.push([0, 14 - i])
for (let i = 0; i < 6; i++) RING.push([i, 8])
for (let i = 0; i < 6; i++) RING.push([6 + i, 8 - i < 0 ? 0 : 8])

// Each color's entry square onto the shared ring (classic Ludo layout: Red
// enters at ring index 1, then Blue/Green/Yellow each 13 squares further
// around), used only to highlight the four start squares on the board.
const START_INDEXES: Record<string, number> = { RED: 1, BLUE: 14, GREEN: 27, YELLOW: 40 }

function trackPosition(globalIndex: number): [number, number] {
	const idx = ((globalIndex % 52) + 52) % 52
	const p = RING[idx % RING.length]
	return [p[0] * 0.9 + 1, p[1] * 0.9 + 1]
}

export function GamePage() {
	const { roomCode } = useParams<{ roomCode: string }>()
	const navigate = useNavigate()
	const { status, lastMessage, rollDice, movePiece } = useGameSocket(roomCode ?? null)
	const [state, setState] = useState<PublicGameState | null>(null)
	// The signed-in player's own id is known as soon as they authenticated
	// (see api/client.ts authenticate()); no manual "who am I" step needed.
	const myUserId = getCurrentUserId()
	const [validMoves, setValidMoves] = useState<string[]>([])
	const [error, setError] = useState<string | null>(null)

	// Derive UI state purely from server-pushed messages; the client never
	// invents dice, positions, turn, or winner values itself.
	useMemo(() => {
		if (!lastMessage) return
		switch (lastMessage.type) {
			case "GAME_STARTED":
			case "RECONNECT_STATE":
				setState(lastMessage.state as PublicGameState)
				setValidMoves([])
				break
			case "DICE_ROLLED":
				setValidMoves((lastMessage.validMoves as string[]) ?? [])
				setState((prev) =>
					prev
						? { ...prev, currentTurn: { ...prev.currentTurn, diceResult: lastMessage.dice as number, status: "WAITING_FOR_PIECE" }, version: lastMessage.version as number }
						: prev,
				)
				haptic("medium")
				break
			case "PIECE_MOVED":
				setState((prev) => {
					if (!prev) return prev
					const pieces = prev.pieces.map((p) => (p.id === lastMessage.pieceId ? { ...p, position: lastMessage.toPosition as number } : p))
					return { ...prev, pieces, version: lastMessage.version as number }
				})
				setValidMoves([])
				haptic("light")
				break
			case "TURN_CHANGED":
				setState((prev) =>
					prev
						? { ...prev, currentTurn: { turnNumber: lastMessage.turnNumber as number, playerId: lastMessage.playerId as string, status: "WAITING_FOR_ROLL", diceResult: null, startedAt: Date.now() }, version: lastMessage.version as number }
						: prev,
				)
				setValidMoves([])
				break
			case "GAME_FINISHED":
				navigate(`/result?winner=${lastMessage.winnerUserId}&room=${roomCode}`)
				break
			case "ERROR":
				setError(lastMessage.message as string)
				setTimeout(() => setError(null), 3000)
				break
			case "ROOM_STATE":
				// Room-level roster update; full game state arrives via GAME_STARTED/RECONNECT_STATE.
				break
		}
	}, [lastMessage, navigate, roomCode])

	const isMyTurn = !!state && !!myUserId && state.currentTurn.playerId === myUserId
	const canRoll = isMyTurn && state?.currentTurn.status === "WAITING_FOR_ROLL"
	const waitingForPieceChoice = isMyTurn && state?.currentTurn.status === "WAITING_FOR_PIECE" && validMoves.length > 0

	const onRoll = useCallback(() => {
		haptic("light")
		rollDice()
	}, [rollDice])

	const onPieceClick = useCallback(
		(pieceId: string) => {
			if (!validMoves.includes(pieceId)) return
			haptic("medium")
			movePiece(pieceId)
		},
		[validMoves, movePiece],
	)

	if (!state) {
		return (
			<div className="page game-page">
				<div className={`connection-status ${status}`}>{status === "connected" ? "🟢 متصل" : status === "reconnecting" ? "🔴 قطع اتصال، در حال اتصال مجدد…" : "در حال اتصال…"}</div>
				<p>در حال بارگذاری وضعیت بازی…</p>
			</div>
		)
	}

	return (
		<div className="page game-page">
			<div className={`connection-status ${status}`}>
				{status === "connected" ? "🟢 متصل" : status === "reconnecting" ? "🔴 قطع اتصال، در حال اتصال مجدد…" : "در حال اتصال…"}
			</div>

			{error && <div className="error-toast">{error}</div>}

			<div className="players-row">
				{state.players.map((p) => (
					<div key={p.userId} className={`player-chip ${p.userId === state.currentTurn.playerId ? "active-turn" : ""}`} style={{ borderColor: COLORS[p.color] }}>
						<span className="dot" style={{ background: COLORS[p.color] }} />
						{p.username} ({p.finishedPieces}/4)
					</div>
				))}
			</div>

			{isMyTurn && <div className="your-turn-banner">🎯 نوبت شماست!</div>}

			<svg viewBox="0 0 15 15" className="ludo-board">
				{/* Light board like classic printed Ludo boards, not a dark app screen */}
				<rect x="0" y="0" width="15" height="15" fill="#ffffff" />
				<rect x="0.08" y="0.08" width="14.84" height="14.84" fill="none" stroke="#111" strokeWidth="0.12" />

				{/* Four player yards, solid color like a real board */}
				<rect x="0" y="0" width="6" height="6" fill={COLORS.RED} />
				<rect x="9" y="0" width="6" height="6" fill={COLORS.BLUE} />
				<rect x="9" y="9" width="6" height="6" fill={COLORS.GREEN} />
				<rect x="0" y="9" width="6" height="6" fill={COLORS.YELLOW} />
				{/* White inner panel inside each yard, matching the reference board */}
				<rect x="1" y="1" width="4" height="4" rx="0.3" fill="#ffffff" />
				<rect x="10" y="1" width="4" height="4" rx="0.3" fill="#ffffff" />
				<rect x="10" y="10" width="4" height="4" rx="0.3" fill="#ffffff" />
				<rect x="1" y="10" width="4" height="4" rx="0.3" fill="#ffffff" />

				{/* Each yard's four token slots, drawn as little circular sockets so the corner doesn't look like a bare empty square */}
				{Object.entries(HOME_SLOTS).map(([color, slots]) =>
					slots.map(([sx, sy], i) => (
						<circle key={`${color}-slot-${i}`} cx={sx} cy={sy} r={0.55} fill="#ffffff" stroke={COLORS[color]} strokeWidth={0.1} />
					)),
				)}

				{/* 52-square shared track, drawn as white circular cells on light gray squares, using the exact same coordinates pieces move along */}
				{RING.map(([cx, cy], idx) => {
					const isStart = Object.values(START_INDEXES).includes(idx)
					const startColor = (Object.entries(START_INDEXES).find(([, i]) => i === idx) ?? [])[0]
					const x = cx * 0.9 + 1
					const y = cy * 0.9 + 1
					return (
						<g key={`track-${idx}`}>
							<rect x={x - 0.45} y={y - 0.45} width={0.9} height={0.9} fill={isStart ? COLORS[startColor as string] : "#ffffff"} stroke="#333" strokeWidth={0.03} />
							{!isStart && <circle cx={x} cy={y} r={0.32} fill="none" stroke="#ccc" strokeWidth={0.03} />}
						</g>
					)
				})}

				{/* Direction-of-travel arrows, one per arm entrance, matching the reference board's turn markers */}
				{([
					{ idx: 0, rotate: 90, color: COLORS.YELLOW },
					{ idx: 13, rotate: 180, color: COLORS.RED },
					{ idx: 26, rotate: 270, color: COLORS.BLUE },
					{ idx: 39, rotate: 0, color: COLORS.GREEN },
				] as const).map(({ idx, rotate, color }) => {
					const [cx, cy] = RING[idx]
					const x = cx * 0.9 + 1
					const y = cy * 0.9 + 1
					return (
						<polygon
							key={`arrow-${idx}`}
							points="-0.3,-0.3 0.3,-0.3 0,0.35"
							fill={color}
							transform={`translate(${x} ${y}) rotate(${rotate})`}
						/>
					)
				})}

				{/* Center home triangle */}
				<rect x="6" y="6" width="3" height="3" fill="#ffffff" stroke="#333" strokeWidth="0.06" />
				<polygon points="6,6 7.5,7.5 6,9" fill={COLORS.RED} />
				<polygon points="6,6 9,6 7.5,7.5" fill={COLORS.BLUE} />
				<polygon points="9,6 9,9 7.5,7.5" fill={COLORS.GREEN} />
				<polygon points="6,9 9,9 7.5,7.5" fill={COLORS.YELLOW} />

				{state.pieces.map((piece) => {
					const [x, y] = piece.state === "HOME" ? HOME_SLOTS[piece.color][Number(piece.id.split("-")[1]) % 4] : trackPosition(piece.position)
					const movable = validMoves.includes(piece.id)
					return (
						<circle
							key={piece.id}
							cx={x}
							cy={y}
							r={movable ? 0.55 : 0.45}
							fill={COLORS[piece.color]}
							stroke={movable ? "#fff" : "#00000055"}
							strokeWidth={movable ? 0.12 : 0.05}
							onClick={() => onPieceClick(piece.id)}
							style={{ cursor: movable ? "pointer" : "default", transition: "cx 0.25s ease, cy 0.25s ease" }}
						/>
					)
				})}
			</svg>

			<div className="dice-area">
				<div className="dice-face">{state.currentTurn.diceResult ?? "–"}</div>
				<button onClick={onRoll} disabled={!canRoll}>
					🎲 {canRoll ? "ریختن تاس" : isMyTurn ? "منتظر انتخاب مهره…" : "نوبت حریف"}
				</button>
				{waitingForPieceChoice && <p className="hint">مهره‌های قابل حرکت روشن شدند — یکی رو بز����</p>}
				{isMyTurn && state.currentTurn.status === "WAITING_FOR_PIECE" && validMoves.length === 0 && <p className="hint">حرکت مجازی وجود ندارد — نوبت به‌زودی رد می‌شود</p>}
			</div>
		</div>
	)
}